// src/pages/admin/ReferralCodeManager.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import {
  collection,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  doc,
} from 'firebase/firestore';
import {
  ArrowLeft,
  Plus,
  Edit2,
  Trash2,
  X,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Copy,
  Check,
  Info,
} from 'lucide-react';

// ─── Constants ────────────────────────────────────────────────────────────────

const CODE_TYPES = [
  { value: 'staff',     label: 'Staff' },
  { value: 'affiliate', label: 'Affiliate' },
  { value: 'campaign',  label: 'Campaign' },
  { value: 'partner',   label: 'Partner' },
];

const codeTypeLabel = (v) => CODE_TYPES.find(t => t.value === v)?.label ?? v;

const EMPTY_CODE_FORM = {
  code: '',
  label: '',
  type: 'staff',
  ownerName: '',
  ownerContact: '',
  active: true,
  notes: '',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function slugify(str) {
  return (str || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// ─── Copy Link Button ───────────────────────────────────────────────────────
// Same clipboard pattern as the CopyableId component used elsewhere in the
// admin pages (ClassManager, AttendanceManager, AdminDashboard).

function CopyLinkButton({ code }) {
  const [copied, setCopied] = useState(false);
  const link = `${window.location.origin}/t/${code}`;

  function handleCopy(e) {
    e.stopPropagation();
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <button
      onClick={handleCopy}
      title={link}
      className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-50 transition-colors"
    >
      {copied ? <Check size={13} className="text-green-500" /> : <Copy size={13} />}
      {copied ? 'Copied' : 'Copy Link'}
    </button>
  );
}

// ─── Status pill ──────────────────────────────────────────────────────────────

function ActivePill({ active }) {
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}>
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

// ─── Code Form Modal (create / edit) ───────────────────────────────────────────
// The doc id IS the code — immutable once created, so the code field is
// editable only on create.

function CodeFormModal({ codeDoc, onSave, onClose, saving, formError }) {
  const isEditing = !!codeDoc?.id;

  const [form, setForm] = useState(
    codeDoc
      ? {
          code: codeDoc.id,
          label: codeDoc.label ?? '',
          type: codeDoc.type ?? 'staff',
          ownerName: codeDoc.ownerName ?? '',
          ownerContact: codeDoc.ownerContact ?? '',
          active: codeDoc.active ?? true,
          notes: codeDoc.notes ?? '',
        }
      : { ...EMPTY_CODE_FORM }
  );
  const [errors, setErrors] = useState({});

  function setField(field, value) {
    setForm(f => ({ ...f, [field]: value }));
    setErrors(e => { const n = { ...e }; delete n[field]; return n; });
  }

  const slugPreview = slugify(form.code);

  function validate() {
    const e = {};
    if (!slugPreview) e.code = 'Enter a code (letters, numbers, hyphens).';
    if (!form.label.trim()) e.label = 'Label is required.';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;
    onSave({ ...form, code: slugPreview, id: codeDoc?.id ?? null });
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">
            {isEditing ? 'Edit Referral Code' : 'New Referral Code'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Code <span className="text-red-500">*</span>
            </label>
            {isEditing ? (
              <p className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm font-mono text-gray-500">
                {form.code}
              </p>
            ) : (
              <>
                <input
                  type="text"
                  value={form.code}
                  onChange={e => setField('code', e.target.value)}
                  placeholder="e.g. sara or ustadha-noor"
                  className={`w-full border rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.code ? 'border-red-400' : 'border-gray-300'}`}
                />
                {slugPreview && slugPreview !== form.code && (
                  <p className="text-xs text-gray-400 mt-1">Will be saved as <span className="font-mono">{slugPreview}</span></p>
                )}
                {errors.code && <p className="text-xs text-red-500 mt-1">{errors.code}</p>}
              </>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Label <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.label}
              onChange={e => setField('label', e.target.value)}
              placeholder="e.g. Sara (staff)"
              className={`w-full border rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.label ? 'border-red-400' : 'border-gray-300'}`}
            />
            {errors.label && <p className="text-xs text-red-500 mt-1">{errors.label}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={form.type}
              onChange={e => setField('type', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              {CODE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Owner name</label>
              <input
                type="text"
                value={form.ownerName}
                onChange={e => setField('ownerName', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Owner contact</label>
              <input
                type="text"
                value={form.ownerContact}
                onChange={e => setField('ownerContact', e.target.value)}
                placeholder="email or phone"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={e => setField('notes', e.target.value)}
              rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
            />
          </div>

          {isEditing && (
            <div className="flex items-center justify-between py-2 border-t border-gray-100">
              <div>
                <p className="text-sm font-medium text-gray-700">Active</p>
                <p className="text-xs text-gray-400">Inactive codes won't appear in the trial-form dropdown</p>
              </div>
              <button
                type="button"
                onClick={() => setField('active', !form.active)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.active ? 'bg-purple-600' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${form.active ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
          )}

          {formError && (
            <p className="text-xs text-red-500 flex items-center gap-1.5"><AlertTriangle size={12} />{formError}</p>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              {saving && <RefreshCw size={14} className="animate-spin" />}
              {isEditing ? 'Save Changes' : 'Create Code'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

function ReferralCodeManager() {
  const navigate = useNavigate();

  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const [formModal, setFormModal] = useState(null); // null | { mode: 'add' } | { mode: 'edit', codeDoc }
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  function showSuccess(msg) {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 4000);
  }

  const fetchAll = async () => {
    try {
      setLoading(true);
      setError(null);
      const snap = await getDocs(collection(db, 'referralCodes'));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (a.label || '').localeCompare(b.label || ''));
      setCodes(list);
    } catch (err) {
      console.error('ReferralCodeManager fetch error:', err);
      setError('Failed to load referral codes. Please refresh.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // ── Save (create or edit) ────────────────────────────────────────────────────

  const handleSaveCode = async (form) => {
    setSaving(true);
    setFormError(null);
    try {
      const isEditing = !!form.id;

      if (!isEditing) {
        // Authoritative duplicate check against Firestore, not just loaded state —
        // setDoc would otherwise silently overwrite an existing code.
        const existing = await getDoc(doc(db, 'referralCodes', form.code));
        if (existing.exists()) {
          setFormError(`Code "${form.code}" already exists. Choose a different one.`);
          setSaving(false);
          return;
        }
      }

      const payload = {
        code: isEditing ? form.id : form.code,
        label: form.label.trim(),
        type: form.type,
        ownerName: form.ownerName?.trim() || null,
        ownerContact: form.ownerContact?.trim() || null,
        active: form.active,
        notes: form.notes?.trim() || null,
        commission: null,
      };

      if (isEditing) {
        await updateDoc(doc(db, 'referralCodes', form.id), payload);
        showSuccess(`${payload.label} updated.`);
      } else {
        payload.createdAt = new Date().toISOString();
        await setDoc(doc(db, 'referralCodes', form.code), payload);
        showSuccess(`${payload.label} created.`);
      }

      setFormModal(null);
      await fetchAll();
    } catch (err) {
      console.error('Save referral code error:', err);
      setFormError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Toggle active ────────────────────────────────────────────────────────────

  const handleToggleActive = async (codeDoc) => {
    setActionLoading(codeDoc.id);
    try {
      await updateDoc(doc(db, 'referralCodes', codeDoc.id), { active: !codeDoc.active });
      showSuccess(`${codeDoc.label} ${codeDoc.active ? 'deactivated' : 'reactivated'}.`);
      await fetchAll();
    } catch (err) {
      console.error('Toggle active error:', err);
      setError('Failed to update code.');
    } finally {
      setActionLoading(null);
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────────────

  const handleDelete = async (codeDoc) => {
    if (!window.confirm(
      `Permanently delete "${codeDoc.code}" (${codeDoc.label})?\n\n` +
      `This cannot be undone. Any trial already attributed to this code will keep ` +
      `the code string on its record, but it will no longer resolve to an owner ` +
      `or type here.\n\n` +
      `Consider deactivating instead — it hides the code from new trials without ` +
      `losing this history.`
    )) return;

    setActionLoading(codeDoc.id);
    try {
      await deleteDoc(doc(db, 'referralCodes', codeDoc.id));
      showSuccess(`${codeDoc.label} deleted.`);
      await fetchAll();
    } catch (err) {
      console.error('Delete referral code error:', err);
      setError('Failed to delete code.');
    } finally {
      setActionLoading(null);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading referral codes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto p-6 space-y-5">

        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin')} className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Referral Codes</h1>
              <p className="text-sm text-gray-500">{codes.length} code{codes.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <button
            onClick={() => { setFormError(null); setFormModal({ mode: 'add' }); }}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-xl hover:bg-purple-700 transition-colors"
          >
            <Plus size={16} />New Code
          </button>
        </div>

        {/* Flash messages */}
        {successMessage && (
          <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
            <CheckCircle size={16} />{successMessage}
          </div>
        )}
        {error && (
          <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            <AlertTriangle size={16} />{error}
            <button onClick={() => setError(null)} className="ml-auto"><X size={14} /></button>
          </div>
        )}

        <div className="flex items-start gap-2 px-4 py-3 bg-indigo-50 border border-indigo-200 rounded-xl text-xs text-indigo-700">
          <Info size={14} className="mt-0.5 flex-shrink-0" />
          <span>
            Links look like <span className="font-mono">{window.location.origin}/t/&lt;code&gt;</span> — that page
            isn't live until Phase 1. Codes created now will already be available for admin-logged trials and the
            landing page once it ships.
          </span>
        </div>

        {/* List */}
        {codes.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <p className="text-gray-500 font-medium">No referral codes yet</p>
            <p className="text-sm text-gray-400 mt-1">Create one to start attributing trials to a staff member, affiliate, or campaign.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Code</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Label</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Type</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Owner</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {codes.map(c => {
                  const isLoading = actionLoading === c.id;
                  return (
                    <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 font-mono text-gray-700">{c.id}</td>
                      <td className="px-4 py-3 text-gray-800">{c.label}</td>
                      <td className="px-4 py-3 text-gray-600">{codeTypeLabel(c.type)}</td>
                      <td className="px-4 py-3 text-gray-500">
                        {c.ownerName || '—'}
                        {c.ownerContact && <span className="text-xs text-gray-400"> · {c.ownerContact}</span>}
                      </td>
                      <td className="px-4 py-3"><ActivePill active={c.active} /></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1.5 flex-wrap">
                          <CopyLinkButton code={c.id} />
                          <button
                            onClick={() => { setFormError(null); setFormModal({ mode: 'edit', codeDoc: c }); }}
                            className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit2 size={15} />
                          </button>
                          <button
                            onClick={() => handleToggleActive(c)}
                            disabled={isLoading}
                            className="px-2.5 py-1.5 text-xs font-medium text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                            title={c.active ? 'Deactivate' : 'Reactivate'}
                          >
                            {isLoading ? <RefreshCw size={13} className="animate-spin" /> : (c.active ? 'Deactivate' : 'Reactivate')}
                          </button>
                          <button
                            onClick={() => handleDelete(c)}
                            disabled={isLoading}
                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

      </div>

      {formModal && (
        <CodeFormModal
          codeDoc={formModal.mode === 'edit' ? formModal.codeDoc : null}
          onSave={handleSaveCode}
          onClose={() => setFormModal(null)}
          saving={saving}
          formError={formError}
        />
      )}
    </div>
  );
}

export default ReferralCodeManager;
